<template>
  <h1 class="result-title">Credit and free entries</h1>
  <div class="content" v-if="freeEntriesCreditBalance">
    <div class="grid">
      <div class="inputs">
        <label>Free Entries Balance:</label>
        <input disabled v-model="freeEntries" readonly />
      </div>

      <select class="dropdown" v-model="selectedOption">
        <option disabled :value="null">Select</option>
        <option key="FreeEntries" value="FreeEntries">Free Entries</option>
        <option key="CompetitionCredit" value="CompetitionCredit">
          Competition Credit
        </option>
      </select>

      <div class="inputs">
        <label>Competition Credit Balance:</label>
        <input disabled v-model="creditBalance" readonly />
      </div>

      <div class="inputs">
        <label for="amount"><strong>Amount:</strong></label>
        <input
          id="amount"
          name="amount"
          v-model="amount"
          placeholder="Enter amount"
        />
      </div>

      <button
        :disabled="isSubmitting"
        class="btn"
        @click="addFreeEntriesOrCreditBalance"
      >
        <span v-if="!isSubmitting">Add</span>
        <LoaderDots v-if="isSubmitting" />
      </button>
    </div>
  </div>

  <div class="results">
    <div class="result-title">Credit Transactions</div>
    <div class="table-content">
      <table>
        <thead>
          <tr class="table-header">
            <th>Transaction Id</th>
            <th>Action</th>
            <th>Order No</th>
            <th>Admin User</th>
            <th>Amount</th>
            <th>Date</th>
            <th>Expiry Date</th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="table-row"
            v-for="creditTransaction in freeEntriesCreditTransactions?.userCreditTransactions"
            :key="creditTransaction.transactionId"
          >
            <td>{{ creditTransaction.transactionId }}</td>
            <td>{{ creditTransaction.action }}</td>
            <td>{{ creditTransaction.orderNo }}</td>
            <td>{{ creditTransaction.adminUser }}</td>
            <td>{{ creditTransaction.amount.toFixed(2) }}</td>
            <td>{{ creditTransaction.date }}</td>
            <td>{{ creditTransaction.expiryDate }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="results">
    <div class="result-title">Free Entry Transactions</div>
    <div class="table-content">
      <table>
        <thead>
          <tr class="table-header">
            <th>Transaction Id</th>
            <th>Action</th>
            <th>Order No</th>
            <th>Admin User</th>
            <th>Amount</th>
            <th>Date</th>
            <th>Expiry Date</th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="table-row"
            v-for="freeEntryTransaction in freeEntriesCreditTransactions?.userFreeEntryTransactions"
            :key="freeEntryTransaction.transactionId"
          >
            <td>{{ freeEntryTransaction.transactionId }}</td>
            <td>{{ freeEntryTransaction.action }}</td>
            <td>{{ freeEntryTransaction.orderNo }}</td>
            <td>{{ freeEntryTransaction.adminUser }}</td>
            <td>{{ freeEntryTransaction.amount.toFixed(2) }}</td>
            <td>{{ freeEntryTransaction.date }}</td>
            <td>{{ freeEntryTransaction.expiryDate }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import AddUserFreeEntriesRequest from "../requests/AddUserFreeEntriesRequest";
import AddUserCreditBalanceRequest from "../requests/AddUserCreditBalanceRequest";
import UserFreeEntriesAndCreditBalanceResponse from "../responses/UserFreeEntriesAndCreditBalanceResponse";
import UserFreeEntryAndCreditTransactionResponse from "../responses/UserFreeEntryAndCreditTransactionResponse";
import { showAlert } from "../utils/global";
import UserService from "../services/user-service";
import LoaderDots from "./LoadingButtonDots.vue";

export default defineComponent({
  components: {
    LoaderDots,
  },
  setup() {
    const route = useRoute();
    const userId = Number(route.params.userId);
    const selectedOption = ref<string | null>(null);
    const freeEntriesCreditBalance =  ref<UserFreeEntriesAndCreditBalanceResponse>();
    const addUserFreeEntriesRequest = ref<AddUserFreeEntriesRequest>();
    const addUserCreditBalanceRequest = ref<AddUserCreditBalanceRequest>();
    const amount = ref("");
    const freeEntries = ref<number | null>(null);
    const creditBalance = ref<string | null>(null);
    const isSubmitting = ref(false);

    const freeEntriesCreditTransactions = ref<UserFreeEntryAndCreditTransactionResponse>({
      isSuccess: false,
      message: "",
      userFreeEntryTransactions: [],
      userCreditTransactions: [],
    });

    const getFreeEntriesAndCreditBalance = async () => {
      freeEntriesCreditBalance.value =
        await UserService.getUserFreeEntriesAndCreditBalance(userId);
      if (freeEntriesCreditBalance.value) {
        freeEntries.value =
          freeEntriesCreditBalance.value.freeEntriesBalance != undefined
            ? freeEntriesCreditBalance.value.freeEntriesBalance
            : 0;

        creditBalance.value =
          freeEntriesCreditBalance.value.creditBalance != undefined
            ? freeEntriesCreditBalance.value.creditBalance.toFixed(2)
            : "0.00";
      }
    };

    const getFreeEntriesAndCreditTransactions = async () => {
      var result = await UserService.getUserFreeEntryAndCreditTransactions(userId);
      
      if (result.isSuccess) {
        freeEntriesCreditTransactions.value.userFreeEntryTransactions = result.userFreeEntryTransactions;
        freeEntriesCreditTransactions.value.userCreditTransactions = result.userCreditTransactions;
      }else{
        showAlert(
          "Failed to load transactions",
          "Failed to load free entries and credit transactions. Please try again later.",
          "error"
        );
      }
    };

    const addFreeEntriesOrCreditBalance = async () => {
      try {
        isSubmitting.value = true;

        if (selectedOption.value == "CompetitionCredit") {
          const validCreditAmountResponse = isValidAmount(
            "CompetitionCredit",
            amount.value
          );
          if (validCreditAmountResponse != "valid") {
            showAlert("Invalid Amount", validCreditAmountResponse, "error");
            isSubmitting.value = false;
            return;
          }

          addUserCreditBalanceRequest.value = {
            userId: userId,
            creditBalance: parseFloat(amount.value),
          };

          const response = await UserService.addOrUpdateUserCreditBalance(
            addUserCreditBalanceRequest.value
          );
          if (!response.isSuccess) {
            selectedOption.value = null;
            amount.value = null;
            isSubmitting.value = false;
            showAlert(
              "Failed to Add Balance",
              "Failed to update balance. Please check the input and try again.",
              "error"
            );

            return;
          }
        } else if (selectedOption.value == "FreeEntries") {
          const validFreeEntryAmountResponse = isValidAmount(
            "FreeEntries",
            amount.value
          );
          if (validFreeEntryAmountResponse != "valid") {
            showAlert("Invalid Amount", validFreeEntryAmountResponse, "error");
            isSubmitting.value = false;
            return;
          }

          addUserFreeEntriesRequest.value = {
            userId: userId,
            freeEntries: Number(amount.value),
          };
          const response = await UserService.addOrUpdateUserFreeEntries(
            addUserFreeEntriesRequest.value
          );

          if (!response.isSuccess) {
            selectedOption.value = null;
            amount.value = null;
            isSubmitting.value = false;
            showAlert(
              "Failed to Add Balance",
              "Failed to update balance. Please check the input and try again.",
              "error"
            );

            return;
          }
        } else {
          isSubmitting.value = false;
          showAlert(
            "Please select a valid option.",
            "Please select a valid option.",
            "error"
          );
          return;
        }

        await getFreeEntriesAndCreditBalance();
        await getFreeEntriesAndCreditTransactions();
        selectedOption.value = null;
        amount.value = null;

        showAlert(
          "Balance updated successfully.",
          "Balance updated successfully",
          "success"
        );

        isSubmitting.value = false;
      } catch (error: any) {
        selectedOption.value = null;
        amount.value = null;

        showAlert(
          "Failed to Add Balance",
          "Failed to update balance. Please check the input and try again.",
          "error"
        );
        isSubmitting.value = false;
      }
    };

    onMounted(async () => {
      await getFreeEntriesAndCreditBalance();
      await getFreeEntriesAndCreditTransactions();
    });

    const isValidAmount = (type: string, amount: string): string => {
      if (amount == null || amount.trim() === "") {
        return "Please enter a valid amount.";
      }

      if (type === "CompetitionCredit") {
        const decimalPattern = /^-?\d+\.\d{2}$/; // Allow negative or positive decimals with exactly 2 decimal places
        if (!decimalPattern.test(amount)) {
          return "Please enter a valid decimal amount (e.g. 10.50 or -5.00).";
        }

        const currentBalance = parseFloat(creditBalance.value ?? "0");
        const inputAmount = parseFloat(amount);

        if (currentBalance + inputAmount < 0) {
          return "This will reduce credit balance below zero. Please adjust the amount.";
        }
      } else if (type === "FreeEntries") {
        const wholeNumberPattern = /^-?\d+$/; // Allow negative or positive whole numbers only (no decimals)
        if (!wholeNumberPattern.test(amount)) {
          return "Please enter a whole number for free entries (no decimals).";
        }

        const currentFreeEntries = freeEntries.value ?? 0;
        const inputAmount = Number(amount);

        if (currentFreeEntries + inputAmount < 0) {
          return "This will reduce free entries below zero. Please adjust the amount.";
        }
      }

      return "valid";
    };

    return {
      freeEntriesCreditBalance,
      freeEntriesCreditTransactions,
      userId,
      addFreeEntriesOrCreditBalance,
      selectedOption,
      amount,
      freeEntries,
      creditBalance,
      isSubmitting,
    };
  },
});
</script>

<style scoped>
.result-title {
  position: relative;
  min-height: 89px;
  align-content: center;
  background-color: var(--clr-primary);
  padding: 1rem 1rem;
  margin-inline: 2rem;
  color: white;
  font-size: 24px;
  border-radius: 3px;
  box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42),
    0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.content {
  overflow-x: auto;
  position: relative;
  background-color: #fff;
  padding: 80px 40px 20px 40px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
  border-radius: 3px;
  margin-top: -80px;
  padding-inline: 10px;
}

label {
  position: absolute;
  left: 10px;
  top: -10px;
  background-color: white;
  padding: 0rem 0.3rem;
}

input {
  display: flex;
  padding: 1rem;
  width: 100%;
  height: 60px;
}

.inputs {
  position: relative;
}
.dropdown {
  font-size: 16px;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #ccc;
  background-color: #fff;
  color: #333;
}

/* This targets the options */
.dropdown option {
  background-color: #fff;
  color: #333;
  font-size: 18px;
  padding: 10px;
}

.btn {
  background-color: var(--clr-primary);
  padding: 1.3rem 1rem;
  color: white;
  font-size: 24px;
  border-radius: 3px;
  border: none;
  height: 69px;
  cursor: pointer;
}

.table-content {
  overflow-x: auto;
  position: relative;
  background-color: #fff;
  padding: 60px 40px 20px 40px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
  border-radius: 3px;
  margin-top: -50px;
}

table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
}

td {
  padding: 0.75rem 1rem;
  text-align: left;
  border-top: 1px solid #ddd;
}

th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: bold;
}

.table-row:hover {
  background-color: #f1f1f1;
}

@media (max-width: 768px) {
  .form-box {
    padding: 10px;
  }

  th,
  td {
    padding: 0.5rem 0.75rem;
  }

  .results {
    overflow-x: scroll;
  }

  .result-title {
    padding: 0.5rem 1rem;
    text-align: center;
    font-size: 16px;
    margin-inline: 0.7rem;
  }

  .table-content {
    padding-inline: 10px;
    margin-top: -30px;
  }

  .search-section {
    padding: 10px;
  }
}
</style>
